/* The rules behind the number, and the path from one of them to the records that failed.
 *
 * This is the last rung of the drill-down — score → dimension → rule → records — and the rung that
 * stops the whole screen being a set of dead-end metrics. The table is the decomposition sorted by
 * what each rule *cost*, because "what cost us" is the question a review asks; the sum of that
 * column is exactly the gap between this score and 100, which is checked when the score is written
 * and again when it is read.
 *
 * **The records themselves are `core.rules`' to show, not this module's.** `ViolationsPanel` already
 * renders masked samples under the tenant's own sample policy, with the rung-4 controls that go with
 * them — a per-tenant key, a retention sweep, an audit entry naming whoever opened one. Rebuilding
 * that here would be a second, unaudited way to reach customer-derived rows. So the score hands over
 * the `execution_id` it already carries and the panel does the rest.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ViolationsPanel } from '@/features/rules';

import { EmptyState } from '@/shared/ui/EmptyState';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import type { Dimension, Score } from '../api/scoring';
import { shareOfDeficit } from '../model/presentation';

export interface FailedChecksProps {
  readonly score: Score;
  /** Pre-selects a dimension when a reader arrived from the diagnosis line. */
  readonly focusDimension?: Dimension | undefined;
}

const SEVERITY_TONE = {
  blocker: 'danger',
  error: 'warn',
  warning: 'info',
  info: 'neutral',
} as const;

export function FailedChecks({ score, focusDimension }: FailedChecksProps): ReactElement {
  const { t } = useTranslation();
  const [dimension, setDimension] = useState<Dimension | ''>(focusDimension ?? '');
  const [showRecords, setShowRecords] = useState(false);

  const totalLost = score.contributions.reduce(
    (sum, entry) => sum + entry.points_lost,
    0,
  );

  if (score.contributions.length === 0) {
    return (
      <EmptyState
        title={t('scoring.checks.noneTitle')}
        guidance={
          score.value === null
            ? t('scoring.checks.noneUnscored')
            : t('scoring.checks.noneClean')
        }
      />
    );
  }

  const dimensions = [...new Set(score.contributions.map((entry) => entry.dimension))];
  const rows = [...score.contributions]
    .filter((entry) => dimension === '' || entry.dimension === dimension)
    .sort((left, right) => right.points_lost - left.points_lost);

  return (
    <div className="lq-stack">
      <p className="lq-muted lq-nomargin">
        {t('scoring.checks.lede', { lost: totalLost.toFixed(1) })}
      </p>

      <div className="lq-checks__filter">
        <label className="lq-field lq-field--inline">
          <span className="lq-field__label">{t('scoring.checks.filterLabel')}</span>
          <select
            className="lq-input"
            value={dimension}
            onChange={(event) => {
              setDimension(event.target.value as Dimension | '');
            }}
          >
            <option value="">{t('scoring.checks.allDimensions')}</option>
            {dimensions.map((entry) => (
              <option key={entry} value={entry}>
                {t(`scoring.dimension.${entry}`)}
              </option>
            ))}
          </select>
        </label>
        <span className="lq-muted">
          {t('scoring.checks.showing', {
            shown: rows.length,
            total: score.contributions.length,
          })}
        </span>
      </div>

      <table className="lq-table lq-table--compact">
        <caption className="lq-visually-hidden">
          {t('scoring.decomposition.caption')}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t('scoring.decomposition.column.rule')}</th>
            <th scope="col">{t('scoring.decomposition.column.dimension')}</th>
            <th scope="col">{t('scoring.decomposition.column.severity')}</th>
            <th scope="col" className="lq-table__num">
              {t('scoring.decomposition.column.pass')}
            </th>
            <th scope="col" className="lq-table__num">
              {t('scoring.checks.records')}
            </th>
            <th scope="col" className="lq-table__num">
              {t('scoring.decomposition.column.lost')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => {
            const share = shareOfDeficit(entry.points_lost, totalLost);
            return (
              <tr key={entry.rule_version_id}>
                <td>
                  <span className="lq-mono">{entry.rule_name}</span>
                  {/* The rule *version* beside the name: two scores months apart under one rule
                      name are only comparable if the version behind each is on the record. */}
                  <span className="lq-scores__sub lq-mono">{entry.rule_label}</span>
                </td>
                <td>{t(`scoring.dimension.${entry.dimension}`)}</td>
                <td>
                  <StatusBadge
                    tone={SEVERITY_TONE[entry.severity]}
                    label={t(`scoring.severity.${entry.severity}`)}
                  />
                </td>
                <td className="lq-table__num lq-mono">
                  {(entry.pass_ratio * 100).toFixed(1)}%
                </td>
                <td className="lq-table__num lq-mono">
                  {entry.violation_count.toLocaleString()}
                  <span className="lq-scores__sub">
                    {t('scoring.checks.ofRows', { rows: entry.rows_evaluated })}
                  </span>
                </td>
                <td className="lq-table__num lq-mono">
                  {entry.points_lost.toFixed(2)}
                  {share !== null && <span className="lq-dim"> ({share}%)</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <section>
        <h3 className="lq-drawer__h">{t('scoring.checks.recordsHead')}</h3>
        {showRecords ? (
          /* Owned by `core.rules`, which holds the sample policy, the per-tenant key and the audit
             entry that opening one writes. This module supplies the execution and nothing else. */
          <ViolationsPanel executionId={score.execution_id} />
        ) : (
          <>
            <p className="lq-muted lq-nomargin">{t('scoring.checks.recordsLede')}</p>
            <div className="lq-drawer__actions">
              <button
                type="button"
                className="lq-btn lq-btn--secondary lq-btn--compact"
                onClick={() => {
                  setShowRecords(true);
                }}
              >
                {t('scoring.action.inspectRecords')}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
